"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.newPromise = newPromise;
exports.promiseResolve = promiseResolve;
exports.promiseAll = promiseAll;
exports.promiseEvery = promiseEvery;
exports.promiseDelay = promiseDelay;
exports.lastly = lastly;
exports.using = using;
exports.tryResolve = tryResolve;
exports.promiseMap = promiseMap;
exports.callIfPromiseTakesTooLong = callIfPromiseTakesTooLong;
exports.retry = retry;
exports.cached = cached;
exports.pollWhileFalse = pollWhileFalse;
exports.pollWhileTrue = pollWhileTrue;
exports.wait = wait;
exports.selectMany = selectMany;
var errors_1 = require("../common/errors");
var win_1 = require("../win");
var runnable_1 = require("../common/runnable");
var log_1 = require("../common/log");
var cancel_1 = require("../common/cancel");
var PromiseImpl = (0, win_1.win)().Promise;
var log = log_1.LogManager.getLog("promiseUtils");
function newPromise(func) {
    return new PromiseImpl(func);
}
function promiseResolve(value) {
    return newPromise(function (resolve) { return resolve(value); });
}
function promiseAll(promises) {
    if (Promise["all"])
        return Promise["all"](promises);
    var chain = promiseResolve();
    promises.forEach(function (p) { return (chain = chain.then(function () { return p; })); });
    return chain;
}
// promiseEvery function exists because promiseAll returns non-typed result, which is not very convenient
function promiseEvery(p1, p2, p3, p4, p5, p6, p7) {
    if (Promise["all"])
        return Promise["all"]([p1, p2, p3, p4, p5, p6, p7]);
    var results = [];
    var chain = promiseResolve();
    if (p1)
        chain = chain.then(function () { return p1.then(function (r) { return (results[0] = r); }); });
    if (p2)
        chain = chain.then(function () { return p2.then(function (r) { return (results[1] = r); }); });
    if (p3)
        chain = chain.then(function () { return p3.then(function (r) { return (results[2] = r); }); });
    if (p4)
        chain = chain.then(function () { return p4.then(function (r) { return (results[3] = r); }); });
    if (p5)
        chain = chain.then(function () { return p5.then(function (r) { return (results[4] = r); }); });
    if (p6)
        chain = chain.then(function () { return p6.then(function (r) { return (results[5] = r); }); });
    if (p7)
        chain = chain.then(function () { return p7.then(function (r) { return (results[6] = r); }); });
    return chain.then(function () { return results; });
}
function promiseDelay(ms, value, token) {
    return newPromise(function (resolve, reject) {
        var id = -1;
        var tokenRegistration = token
            ? token.register(function () {
                clearTimeout(id);
                reject(new cancel_1.CancellationError());
            })
            : null;
        id = window.setTimeout(function () {
            tokenRegistration && tokenRegistration.dispose();
            resolve(value);
        }, ms);
    });
}
function lastly(promise, handler) {
    if (promise["lastly"])
        return promise["lastly"](handler);
    return promise.then(function (result) {
        return promiseResolve()
            .then(handler)
            .then(function () { return result; });
    }, function (error) {
        return promiseResolve()
            .then(handler)
            .then(function () { return Promise.reject(error); });
    });
}
function using(resource, resourceUser) {
    var resourcePromise = promiseResolve().then(function () { return resource; });
    return resourcePromise.then(function (resource) {
        return lastly(resourceUser(resource), function () { return resource.dispose(); });
    });
}
function tryResolve(promise, defaultValue) {
    return promise.then(null, function (e) { return defaultValue; });
}
function promiseMap(values, mapper, options) {
    options = options || { concurrency: 1 };
    var pool = new runnable_1.TaskPool(options.concurrency);
    var mapResult = values.map(function () { return void 0; });
    return newPromise(function (resolve, reject) {
        values.forEach(function (value, index, array) {
            return pool
                .run(function () { return mapper(value, index, array.length); })
                .then(function (result) { return (mapResult[index] = result); })
                .catch(function (error) { return reject(error); });
        });
        pool.wait().then(function () { return resolve(mapResult); });
    });
}
function callIfPromiseTakesTooLong(p, callback, waitBeforeCallTime, delayPromiseExecutionTime) {
    delayPromiseExecutionTime = delayPromiseExecutionTime || 500;
    var waitingTimeFinished = false;
    var shouldTriggerCallback = true;
    var timeoutId = setTimeout(function () {
        waitingTimeFinished = true;
        if (shouldTriggerCallback)
            callback();
    }, waitBeforeCallTime);
    var onResolve = function () {
        shouldTriggerCallback = false;
        clearTimeout(timeoutId);
    };
    return p.then(function (result) {
        onResolve();
        return waitingTimeFinished
            ? promiseDelay(delayPromiseExecutionTime).then(function () { return result; })
            : promiseResolve(result);
    }, function (e) {
        onResolve();
        throw e;
    });
}
function retry(f, attempts, shouldRetryPredicate, delayMs) {
    if (delayMs === void 0) { delayMs = 0; }
    shouldRetryPredicate = shouldRetryPredicate
        ? shouldRetryPredicate
        : function (r) { return false; };
    var tryResolve = function () {
        attempts--;
        var retryMessage = "shouldRetryPredicate returned true, f() result is not good enough.";
        return f()
            .then(function (r) {
            return shouldRetryPredicate(r) ? Promise.reject(new Error(retryMessage)) : r;
        })
            .then(null, function (e) {
            if ((0, cancel_1.isCancellationError)(e))
                return Promise.reject(e);
            if (attempts === 0) {
                var retryError = new errors_1.CommonError("Max attempts for retry reached", null, errors_1.ErrorCodes.MaxAttemptsExceeded);
                log.error(retryError.message, retryError);
                return Promise.reject(e);
            }
            if (delayMs)
                return promiseDelay(delayMs).then(function () { return tryResolve(); });
            return tryResolve();
        });
    };
    return tryResolve();
}
function cached(func, expirePeriod, retry) {
    var result;
    var savedThis;
    return function () {
        if (result) {
            if (savedThis !== this)
                log.warn("OOOPS! Invalid usage of cache detected");
            return result;
        }
        var r = func.apply(this, arguments);
        savedThis = this;
        result = r;
        if (expirePeriod) {
            if (r.then) {
                r.then(function () {
                    setTimeout(function () {
                        result = null;
                    }, expirePeriod);
                });
            }
            else
                setTimeout(function () {
                    result = null;
                }, expirePeriod);
        }
        if (retry && r.then) {
            r.then(function () { }, function (err) {
                log.warn(err);
                result = null;
            });
        }
        return r;
    };
}
function pollWhileFalse(func, options, predicate, token) {
    var attempt = 0;
    var check = function () {
        token && token.throwIfCancelled();
        return func().then(function (res) {
            if (predicate(res))
                return promiseResolve(res);
            attempt++;
            if (options.maxAttempts && attempt > options.maxAttempts)
                return Promise.reject("Poller exceeded its max attempts: ".concat(options.maxAttempts));
            return promiseDelay(options.intervalMs, token).then(check);
        });
    };
    if (options.waitFirst)
        return promiseDelay(options.intervalMs, token).then(check);
    return check();
}
function pollWhileTrue(func, options, predicate, token) {
    return pollWhileFalse(func, options, function (data) { return !predicate(data); }, token);
}
function wait(predicate, options, token) {
    var attempt = 0;
    var check = function () {
        token && token.throwIfCancelled();
        if (predicate())
            return promiseResolve();
        attempt++;
        if (options.maxAttempts && attempt > options.maxAttempts) {
            var msg = "Poller exceeded its max attempts: ".concat(options.maxAttempts);
            return Promise.reject(msg);
        }
        return promiseDelay(options.intervalMs, token).then(check);
    };
    if (options.waitFirst)
        return promiseDelay(options.intervalMs, token).then(check);
    return check();
}
function selectMany(array, action) {
    var resultArray = [];
    var chain = new runnable_1.TaskChain();
    array.forEach(function (item) {
        return chain.run(function () { return action(item).then(function (result) { return resultArray.push.apply(resultArray, result); }); });
    });
    return chain.wait().then(function () { return resultArray; });
}
